import React, { useState, useEffect, useCallback } from 'react'
import DataTable from '../components/DataTable'
import Modal from '../components/Modal'
import FormField from '../components/FormField'

interface Item {
  id: number
  item_code: string
  item_name: string
  category: string
  unit: string
  unit_price: number
  cost_price: number
  description: string
  created_at: string
  updated_at: string
}

const emptyItem: Omit<Item, 'id' | 'created_at' | 'updated_at'> = {
  item_code: '',
  item_name: '',
  category: '',
  unit: '式',
  unit_price: 0,
  cost_price: 0,
  description: ''
}

const categories = [
  '材料費',
  '労務費',
  '外注費',
  '設備費',
  '諸経費',
  'その他'
]

const Item: React.FC = () => {
  const [items, setItems] = useState<Item[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<Item | null>(null)
  const [formData, setFormData] = useState(emptyItem)
  const [error, setError] = useState('')

  const loadItems = useCallback(async () => {
    try {
      setLoading(true)
      const result = await window.api.item.list({ page, limit: 20, search: search || undefined })
      if (result.ok) {
        const data = result.data as { rows: Item[]; total: number }
        setItems(data.rows)
        setTotal(data.total)
      } else {
        setError(result.error as string)
      }
    } catch (err) {
      setError(String(err))
    } finally {
      setLoading(false)
    }
  }, [page, search])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    setPage(1)
  }

  const openCreateModal = () => {
    setEditingItem(null)
    setFormData(emptyItem)
    setShowModal(true)
  }

  const openEditModal = (item: Item) => {
    setEditingItem(item)
    setFormData({
      item_code: item.item_code,
      item_name: item.item_name,
      category: item.category,
      unit: item.unit,
      unit_price: item.unit_price,
      cost_price: item.cost_price,
      description: item.description
    })
    setShowModal(true)
  }

  const handleSubmit = async () => {
    try {
      if (editingItem) {
        const result = await window.api.item.update(editingItem.id, formData)
        if (!result.ok) {
          setError(result.error as string)
          return
        }
      } else {
        const result = await window.api.item.create(formData)
        if (!result.ok) {
          setError(result.error as string)
          return
        }
      }
      setShowModal(false)
      loadItems()
    } catch (err) {
      setError(String(err))
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('この品目を削除してもよろしいですか？\n（論理削除されます）')) {
      return
    }
    try {
      const result = await window.api.item.delete(id)
      if (result.ok) {
        loadItems()
      } else {
        setError(result.error as string)
      }
    } catch (err) {
      setError(String(err))
    }
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(price)
  }

  const columns = [
    { key: 'item_code', label: '品目コード', width: 120, align: 'left' as const },
    { key: 'item_name', label: '品目名', width: 200, align: 'left' as const },
    { key: 'category', label: 'カテゴリ', width: 100, align: 'left' as const },
    { key: 'unit', label: '単位', width: 60, align: 'left' as const },
    { 
      key: 'unit_price', 
      label: '単価', 
      width: 120, 
      align: 'right' as const,
      render: (value: number) => formatPrice(value)
    },
    { 
      key: 'cost_price', 
      label: '原価', 
      width: 120, 
      align: 'right' as const,
      render: (value: number) => formatPrice(value)
    }
  ]

  const totalPages = Math.ceil(total / 20)

  return (
    <div className="page item-page">
      <div className="page-header">
        <h2 className="page-title">品目マスタ</h2>
        <button className="btn btn-primary" onClick={openCreateModal}>
          新規品目登録
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="品目コード・品目名で検索..."
          value={search}
          onChange={handleSearch}
          className="search-input"
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        onEdit={openEditModal}
        onDelete={(row) => handleDelete(row.id)}
        emptyMessage="品目データがありません"
      />

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            前へ
          </button>
          <span className="page-info">{page} / {totalPages} ページ</span>
          <button
            className="btn btn-secondary"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            次へ
          </button>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingItem ? '品目情報編集' : '新規品目登録'}
      >
        <div className="form-grid">
          <FormField label="品目コード" required>
            <input
              type="text"
              value={formData.item_code}
              onChange={(e) => setFormData({ ...formData, item_code: e.target.value })}
              className="input"
            />
          </FormField>
          <FormField label="品目名" required>
            <input
              type="text"
              value={formData.item_name}
              onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
              className="input"
            />
          </FormField>
          <FormField label="カテゴリ" required>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="input select"
            >
              <option value="">選択してください</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </FormField>
          <FormField label="単位" required>
            <select
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              className="input select"
            >
              <option value="式">式</option>
              <option value="個">個</option>
              <option value="m">m</option>
              <option value="m2">m²</option>
              <option value="枚">枚</option>
              <option value="本">本</option>
              <option value="セット">セット</option>
              <option value="人日">人日</option>
              <option value="時間">時間</option>
            </select>
          </FormField>
          <FormField label="単価" required>
            <input
              type="number"
              value={formData.unit_price}
              onChange={(e) => setFormData({ ...formData, unit_price: Number(e.target.value) })}
              className="input"
              min={0}
            />
          </FormField>
          <FormField label="原価">
            <input
              type="number"
              value={formData.cost_price}
              onChange={(e) => setFormData({ ...formData, cost_price: Number(e.target.value) })}
              className="input"
              min={0}
            />
          </FormField>
          <FormField label="説明" fullWidth>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input textarea"
              rows={3}
            />
          </FormField>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
            キャンセル
          </button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            {editingItem ? '更新' : '登録'}
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default Item
